import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useCountUp } from '../../../hooks/useCountUp';
import './About.css';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Get stats data from translations
  const stats = t('about.stats', { returnObjects: true }) || [
    { number: 76, label: 'Projects Completed', suffix: '+' },
    { number: 98, label: 'Client Satisfaction', suffix: '%' },
    { number: 4, label: 'Team Members', suffix: '+' },
    { number: 5, label: 'Years Experience', suffix: '' },
  ];

  // Detect when stats section is in view to trigger animation
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  // Create animated counters for each stat
  const count1 = useCountUp(stats[0].number, 2000, stats[0].suffix, inView);
  const count2 = useCountUp(stats[1].number, 2000, stats[1].suffix, inView);
  const count3 = useCountUp(stats[2].number, 2000, stats[2].suffix, inView);
  const count4 = useCountUp(stats[3].number, 2000, stats[3].suffix, inView);

  const animatedCounts = [count1, count2, count3, count4];

  // Get features from translations
  const features = t('about.features', { returnObjects: true }) || [
    {
      title: 'Creative Excellence',
      description: 'Award-winning creative team with proven track record.',
    },
    {
      title: 'Strategic Approach',
      description: 'Data-driven strategies that deliver measurable results.',
    },
    {
      title: 'Client-Focused',
      description: 'Your success is our priority. We listen, adapt, and deliver.',
    },
  ];

  // Feature icons (can be moved to translations if needed)
  const featureIcons = ['🎨', '📊', '🤝'];

  return (
    <section id="about" className="about" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className={`about-content ${isRTL ? 'rtl' : 'ltr'}`}>
          {/* Left Side - Text Content */}
          <motion.div
            className={`about-text ${isRTL ? 'rtl-text' : 'ltr-text'}`}
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="section-tag">{t('about.tag')}</div>
            <h2 className={isRTL ? 'rtl-heading' : 'ltr-heading'}>
              {t('about.title')}{' '}
              <span className="gradient-text">
                {i18n.language === 'ar' ? 'أورفين' : 'Orvin'}
              </span>
            </h2>
            <p className={isRTL ? 'rtl-paragraph' : 'ltr-paragraph'}>
              {t('about.subtitle')}
            </p>

            <div className="features">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className={`feature ${isRTL ? 'rtl-feature' : 'ltr-feature'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <div className="feature-icon">{featureIcons[index]}</div>
                  <div className="feature-content">
                    <h3 className={isRTL ? 'rtl-feature-title' : 'ltr-feature-title'}>
                      {feature.title}
                    </h3>
                    <p className={isRTL ? 'rtl-feature-desc' : 'ltr-feature-desc'}>
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Side - Stats with Animation */}
          <motion.div
            className="about-visual"
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            ref={ref}
          >
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat-card"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, rotate: isRTL ? -2 : 2 }}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <div className={`stat-number ${isRTL ? 'rtl-number' : 'ltr-number'}`}>
                    {animatedCounts[index]}
                  </div>
                  <div className={`stat-label ${isRTL ? 'rtl-label' : 'ltr-label'}`}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="rotating-circles"
              animate={{ rotate: isRTL ? -360 : 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            >
              <div className="circle-ring ring-1" />
              <div className="circle-ring ring-2" />
              <div className="circle-ring ring-3" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;